
import { motion } from 'framer-motion';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Brain, Shield, Zap, Heart, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';
import { getTraitInterpretations } from '@/utils/bigFiveScoring';

interface ResultsInterpretationProps {
  percentiles: {
    O: number;
    C: number;
    E: number;
    A: number;
    N: number;
  };
}

// Component to render markdown-like text with bold and italic
const MarkdownText = ({ text }: { text: string }) => {
  // Simple markdown parser for **bold** and *italic*
  const parseText = (text: string) => {
    const parts = [];
    let currentIndex = 0;
    
    // Regex to find **bold** and *italic* patterns
    const markdownRegex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let match;
    
    while ((match = markdownRegex.exec(text)) !== null) {
      // Add text before the match
      if (match.index > currentIndex) {
        parts.push({
          type: 'text',
          content: text.slice(currentIndex, match.index)
        });
      }
      
      // Add the formatted text
      const matchedText = match[0];
      if (matchedText.startsWith('**') && matchedText.endsWith('**')) {
        parts.push({
          type: 'bold',
          content: matchedText.slice(2, -2)
        });
      } else if (matchedText.startsWith('*') && matchedText.endsWith('*')) {
        parts.push({
          type: 'italic',
          content: matchedText.slice(1, -1)
        });
      }
      
      currentIndex = match.index + matchedText.length;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.slice(currentIndex)
      });
    }
    
    return parts;
  };

  const parts = parseText(text);
  
  return (
    <span>
      {parts.map((part, index) => {
        switch (part.type) {
          case 'bold':
            return <strong key={index}>{part.content}</strong>;
          case 'italic':
            return <em key={index}>{part.content}</em>;
          default:
            return <span key={index}>{part.content}</span>;
        }
      })}
    </span>
  );
};

export default function ResultsInterpretation({ percentiles }: ResultsInterpretationProps) {
  const interpretations = getTraitInterpretations(percentiles);
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  const toggleCard = (traitKey: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [traitKey]: !prev[traitKey]
    }));
  };

  const truncateText = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

  const traits = [
    {
      key: 'O' as const,
      title: 'Abertura à Experiência',
      gradient: 'from-purple-500 to-indigo-600',
      borderColor: 'border-purple-200 hover:border-purple-300',
      bgAccent: 'bg-purple-50',
      icon: Brain,
    },
    {
      key: 'C' as const,
      title: 'Conscienciosidade',
      gradient: 'from-blue-500 to-cyan-600',
      borderColor: 'border-blue-200 hover:border-blue-300',
      bgAccent: 'bg-blue-50',
      icon: Shield,
    },
    {
      key: 'E' as const,
      title: 'Extroversão',
      gradient: 'from-orange-500 to-red-600',
      borderColor: 'border-orange-200 hover:border-orange-300',
      bgAccent: 'bg-orange-50',
      icon: Zap,
    },
    {
      key: 'A' as const,
      title: 'Amabilidade',
      gradient: 'from-emerald-500 to-teal-600',
      borderColor: 'border-emerald-200 hover:border-emerald-300',
      bgAccent: 'bg-emerald-50',
      icon: Heart,
    },
    {
      key: 'N' as const,
      title: 'Neuroticismo',
      gradient: 'from-rose-500 to-pink-600',
      borderColor: 'border-rose-200 hover:border-rose-300',
      bgAccent: 'bg-rose-50',
      icon: Lightbulb,
    },
  ];

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'low':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'average':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'high':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getLevelText = (level: string) => {
    switch (level) {
      case 'low':
        return 'Baixo';
      case 'average':
        return 'Médio';
      case 'high':
        return 'Alto';
      default:
        return 'N/A';
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-3">
          Interpretação dos Resultados
        </h2>
        <p className="text-gray-600 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
          Descubra como seus traços de personalidade se manifestam em diferentes aspectos da sua vida
        </p>
      </div>
      
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        {traits.map((trait, index) => {
          const IconComponent = trait.icon;
          const isExpanded = expandedCards[trait.key];
          const description = interpretations[trait.key].description;
          
          return (
            <motion.div
              key={trait.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group h-full"
            >
              <Card className={`${trait.borderColor} transition-all duration-300 hover:shadow-xl hover:scale-[1.02] ${trait.bgAccent} border-2 overflow-hidden relative h-full flex flex-col`}>
                {/* Gradient header accent */}
                <div className={`h-2 bg-gradient-to-r ${trait.gradient} flex-shrink-0`} />
                
                <CardHeader className="pb-3 relative flex-shrink-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className={`p-3 rounded-xl bg-gradient-to-r ${trait.gradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <CardTitle className="text-base sm:text-lg font-bold text-gray-800 leading-tight break-words">
                          {trait.title}
                        </CardTitle>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge 
                            variant="outline" 
                            className={`text-xs sm:text-sm font-semibold ${getLevelColor(interpretations[trait.key].level)} border flex-shrink-0`}
                          >
                            {getLevelText(interpretations[trait.key].level)}
                          </Badge>
                          <span className="text-xs sm:text-sm font-medium text-gray-600 flex-shrink-0">
                            {Math.round(percentiles[trait.key])}º percentil
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                  <div className="space-y-3 flex-1">
                    {/* Progress bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <motion.div
                        className={`h-full bg-gradient-to-r ${trait.gradient} rounded-full`}
                        initial={{ width: 0 }}
                        animate={{ width: `${percentiles[trait.key]}%` }}
                        transition={{ duration: 1, delay: index * 0.2 }}
                      />
                    </div>
                    
                    {/* Description */}
                    <div className="flex-1 flex flex-col">
                      <p className="text-sm sm:text-base text-gray-700 leading-relaxed text-justify hyphens-auto break-words">
                        <MarkdownText 
                          text={isExpanded ? description : truncateText(description, 200)} 
                        />
                      </p>
                      
                      {/* Toggle button */}
                      {description.length > 200 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleCard(trait.key)}
                          className="mt-3 self-start text-gray-600 hover:text-gray-800 p-1 h-auto"
                        >
                          <span className="text-xs mr-1">
                            {isExpanded ? 'Ver menos' : 'Ver mais'}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-3 h-3" />
                          ) : (
                            <ChevronDown className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
                
                {/* Subtle decorative element */}
                <div className={`absolute top-4 right-4 w-8 h-8 bg-gradient-to-r ${trait.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-300`} />
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
